import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getCMSImageURL } from "../../utils/cms.utils";
import OptimizedImage from "../common/OptimizedImage";
import { parseShowcaseTargetURI } from "../showcase/showcase.utils";
import { useInputMode } from "../../utils/hooks/useInputMode";
import {
  focusHomeMenu,
  resetRowTransformAfterBlur,
  resetRowTransformIfUnfocused,
  syncFocusedRowTransform,
} from "./focusHelpers";

const MaterialItem = ({ item = {}, onFocus, onBlur, isFirst, focusKey }) => {
  const navigate = useNavigate();

  const [hovered, setHovered] = useState(false);
  const { slug, staticRenderImage } = item || {};
  const { focused, ref, focusSelf } = useFocusable({
    focusKey,
    onFocus,
    onBlur,
    onEnterPress: () => navigate(parseShowcaseTargetURI(item.actionLink)),
    onArrowPress: (direction) => {
      if (direction === "right" && isFirst) {
        return focusHomeMenu();
      }
      return true;
    },
  });
  const { setMouseFocus } = useInputMode();
  const handleMouseEnter = useCallback(() => {
    setMouseFocus(true);
    setHovered(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  // Get image URLs with LQIP/WebP support
  const imageData = {
    src: getCMSImageURL(staticRenderImage, {
      width: 720,
      format: "webp",
      quality: 90
    }),
  };

  return (
    <div
      ref={ref}
      onClick={() => navigate(parseShowcaseTargetURI(item.actionLink))}
      className={"legacy-material-card-shell" + (focused || hovered ? " is-focused" : "")}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setHovered(false)}
    >
      <div className={"legacy-static-slider-item" + (focused || hovered ? " is-active" : "")}>
        <OptimizedImage
          className="legacy-material-image"
          alt={slug}
          src={imageData.src}
        />
      </div>
    </div>
  );
};

export const MaterialSlider = ({ staticItems, onFocus, id, index }) => {
  const itemFocusKeyPrefix = "material_slider_item_" + id + "_";
  const { ref, focusKey } = useFocusable({
    focusKey: "material_slider_" + id,
    focusable: false,
    trackChildren: true,
    autoRestoreFocus: true,
    preferredChildFocusKey: itemFocusKeyPrefix + "0",
  });

  const scrollingRef = useRef(null);
  const innerRef = useRef(null);
  const staticItemCount = staticItems ? staticItems.length : 0;

  useLayoutEffect(() => {
    var delays = [0, 80, 220, 600, 1400, 2600];
    resetRowTransformIfUnfocused(innerRef);
    var timers = delays.map(function (delay) {
      return setTimeout(function () {
        resetRowTransformIfUnfocused(innerRef);
      }, delay);
    });

    return function () {
      timers.forEach(function (timer) {
        clearTimeout(timer);
      });
    };
  }, [staticItemCount]);

  const { isMouseFocus } = useInputMode();

  const onAssetFocus = useCallback(
    (props) => {
      if (typeof onFocus === "function") onFocus(props);
      if (isMouseFocus) return; // Skip horizontal scroll on mouse hover
      syncFocusedRowTransform({
        innerRef,
        scrollerRef: scrollingRef,
        leftPadding: 80,
      });
    },
    [scrollingRef, isMouseFocus, onFocus]
  );
  const onAssetBlur = useCallback(
    () => {
      resetRowTransformAfterBlur(innerRef);
    },
    []
  );

  return (
    <FocusContext.Provider value={focusKey}>
      <div ref={ref} tabIndex={index} className="legacy-static-slider-wrapper legacy-material-slider-wrapper">
        <div className="legacy-static-slider-scroller" ref={scrollingRef}>
          <div className="legacy-static-slider-content" ref={innerRef}>
            {staticItems?.map((item, idx) => (
              <MaterialItem
                item={item}
                key={"mtus_" + id + item.staticRenderImage}
                onFocus={onAssetFocus}
                onBlur={onAssetBlur}
                isFirst={idx === 0}
                focusKey={itemFocusKeyPrefix + idx}
              />
            ))}
          </div>
        </div>
      </div>
    </FocusContext.Provider>
  );
};
